import { FormEvent, useEffect, useState } from 'react';

interface RegistrationSectionProps {
  isOpen: boolean;
  onClose: () => void;
}

const specialties = [
  'Невролог',
  'Терапевт',
  'Кардиолог',
  'Реабилитолог',
  'Педиатр',
  'Нейрохирург',
  'Анестезиолог',
  'Клинический фармаколог',
  'Эпилептолог',
  'Логопед',
  'Студент',
  'Ординатор',
  'Другое',
];

const REGISTRATION_ENDPOINT = 'https://painmed.ru/wp-json/school-reg/v1/register';
const EVENT_CITY = 'Санкт-Петербург';

function formatPhone(value: string) {
  let digits = value.replace(/\D/g, '');

  if (!digits) return '';

  if (digits[0] === '8') {
    digits = `7${digits.slice(1)}`;
  }

  if (digits[0] !== '7') {
    digits = `7${digits}`;
  }

  let formatted = '+7';

  if (digits.length > 1) formatted += ` (${digits.slice(1, 4)}`;
  if (digits.length > 4) formatted += `) ${digits.slice(4, 7)}`;
  if (digits.length > 7) formatted += `-${digits.slice(7, 9)}`;
  if (digits.length > 9) formatted += `-${digits.slice(9, 11)}`;

  return formatted;
}

export default function RegistrationSection({ isOpen, onClose }: RegistrationSectionProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [source, setSource] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const currentSource =
      params.get('source') ||
      params.get('utm_source') ||
      sessionStorage.getItem('reg_source') ||
      '';

    setSource(currentSource);

    if (currentSource) {
      sessionStorage.setItem('reg_source', currentSource);
    }
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: String(data.get('name') || '').trim(),
      phone: String(data.get('phone') || '').trim(),
      email: '',
      specialty: String(data.get('specialty') || '').trim(),
      city: String(data.get('city') || EVENT_CITY),
      source: String(data.get('source') || '').trim(),
      website: String(data.get('website') || '').trim(),
    };

    setStatus('loading');

    try {
      const res = await fetch(REGISTRATION_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await res.json().catch(() => null);

      if (res.ok && result?.success) {
        setStatus('success');
        form.reset();
        return;
      }

      setStatus('error');
    } catch {
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#0f3d32]/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-5 flex items-center justify-between rounded-t-2xl">
          <div>
            <h3 className="text-xl font-extrabold text-[#0f3d32]">Регистрация на NeuroRussia 2026</h3>
            <p className="text-sm text-gray-500 mt-0.5">Бесплатно • 16-17 апреля • Санкт-Петербург</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="px-6 py-6">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-[#1a6b5a]/10 rounded-full mx-auto mb-4">
                <i className="ri-check-line text-3xl text-[#1a6b5a]"></i>
              </div>
              <h4 className="text-xl font-bold text-[#0f3d32] mb-2">Вы зарегистрированы!</h4>
              <p className="text-gray-500 text-sm leading-relaxed">
                Спасибо за регистрацию. Мы свяжемся с вами ближе к дате конференции.
              </p>
              <button
                onClick={onClose}
                className="mt-6 bg-[#e8a030] text-white font-bold px-8 py-3 rounded-full hover:bg-[#f0aa35] transition-all cursor-pointer whitespace-nowrap"
                style={{ boxShadow: '0 2px 14px 0 rgba(232,160,48,0.4)' }}
              >
                Закрыть
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <input type="hidden" name="source" value={source} />
              <input type="hidden" name="city" value={EVENT_CITY} />
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="pointer-events-none absolute left-[-9999px] h-0 opacity-0"
              />

              <div>
                <label className="block text-sm font-semibold text-[#0f3d32] mb-2">
                  ФИО <span className="text-[#c8963e]">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Иванов Иван Иванович"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#1a6b5a] transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0f3d32] mb-2">
                  Телефон <span className="text-[#c8963e]">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  inputMode="tel"
                  placeholder="+7 (___) ___-__-__"
                  onInput={(e) => {
                    e.currentTarget.value = formatPhone(e.currentTarget.value);
                  }}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#1a6b5a] transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0f3d32] mb-2">
                  Специальность <span className="text-[#c8963e]">*</span>
                </label>
                <select
                  name="specialty"
                  required
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#1a6b5a] transition-colors bg-white cursor-pointer"
                >
                  <option value="">Выберите специальность</option>
                  {specialties.map((specialty) => (
                    <option key={specialty} value={specialty}>
                      {specialty}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-start gap-3">
                <input type="checkbox" name="consent" id="consent" required className="mt-1 cursor-pointer" />
                <label htmlFor="consent" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
                  Я согласен(а) на обработку персональных данных в соответствии с{' '}
                  <a href="#" className="text-[#c8963e] underline">
                    политикой конфиденциальности
                  </a>
                  .
                </label>
              </div>

              {status === 'error' && (
                <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-600">
                  Не удалось отправить заявку. Пожалуйста, попробуйте ещё раз.
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-[#e8a030] text-white font-extrabold py-4 rounded-full hover:bg-[#f0aa35] transition-all disabled:opacity-60 cursor-pointer whitespace-nowrap text-base hover:scale-[1.02]"
                style={{ boxShadow: '0 4px 20px 0 rgba(232,160,48,0.45)' }}
              >
                {status === 'loading' ? 'Отправляем...' : 'Зарегистрироваться бесплатно'}
              </button>

              <p className="text-center text-xs text-gray-400">
                Регистрируйтесь. Количество мест ограничено.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
